import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building, Users, Award, MapPin, Loader2, Sparkles } from 'lucide-react';
import { useProfile, useFeaturedProjects, useClients, useStats } from '../hooks/usePortfolioData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Portfolio = () => {
  const { profile, loading: profileLoading } = useProfile();
  const { projects: featuredProjects, loading: projectsLoading } = useFeaturedProjects();
  const { clients: clientLogos, loading: clientsLoading } = useClients();
  const { stats: projectStats, loading: statsLoading } = useStats();

  // Show loading state while data loads
  if (profileLoading || statsLoading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" size={48} />
      </div>
    );
  }

  const personalInfo = profile || {};

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-20 px-6 relative overflow-hidden">
        {/* Subtle background animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-gray-50/30"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100/20 to-transparent rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-gray-100/20 to-transparent rounded-full blur-3xl animate-pulse delay-1000"></div>

        <div className="max-w-6xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text">
                {personalInfo.name}
              </h1>
              <div className="space-y-2 mb-6">
                <p className="text-xl text-gray-600">{personalInfo.title}</p>
                <p className="text-lg text-gray-700 font-medium">{personalInfo.subtitle}</p>
                <p className="text-sm text-gray-500">{personalInfo.nationality}</p>
              </div>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {personalInfo.bio}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="transform hover:scale-105 transition-all duration-300 hover:shadow-lg">
                  <Link to="/projects">
                    View Projects <ArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform" size={20} />
                  </Link>
                </Button>
                <Button variant="outline" size="lg" asChild className="transform hover:scale-105 transition-all duration-300 hover:shadow-md">
                  <Link to="/contact">Get In Touch</Link>
                </Button>
              </div>
            </div>
            <div className="lg:pl-8 animate-fade-in-up delay-300">
              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                <h3 className="text-lg font-semibold mb-6 text-gray-900">Quick Stats</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center group">
                    <div className="flex justify-center mb-3">
                      <Building className="text-gray-600 group-hover:text-blue-600 transition-colors duration-300" size={40} />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{projectStats.projectValue}</div>
                    <div className="text-sm text-gray-600 font-medium">Project Value</div>
                  </div>
                  <div className="text-center group delay-100">
                    <div className="flex justify-center mb-3">
                      <Users className="text-gray-600 group-hover:text-blue-600 transition-colors duration-300" size={40} />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{projectStats.totalProjects}</div>
                    <div className="text-sm text-gray-600 font-medium">Projects Delivered</div>
                  </div>
                  <div className="text-center group delay-200">
                    <div className="flex justify-center mb-3">
                      <Award className="text-gray-600 group-hover:text-blue-600 transition-colors duration-300" size={40} />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{projectStats.teamSize}</div>
                    <div className="text-sm text-gray-600 font-medium">Team Members Led</div>
                  </div>
                  <div className="text-center group delay-300">
                    <div className="flex justify-center mb-3">
                      <MapPin className="text-gray-600 group-hover:text-blue-600 transition-colors duration-300" size={40} />
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{projectStats.totalArea}</div>
                    <div className="text-sm text-gray-600 font-medium">m² Delivered</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Projects</h2>
            <p className="text-lg text-gray-600">Recent work showcasing technical excellence and design innovation</p>
          </div>
          {projectsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-gray-400" size={32} />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {featuredProjects.map((project, index) => (
                <Card key={project.slug} className={`overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-fade-in-up group border-0 shadow-md hover:shadow-2xl ${index === 0 ? '' : `delay-${(index + 1) * 100}`}`}>
                  <div className="relative overflow-hidden">
                    {project.images?.main ? (
                      <img
                        src={project.images.main}
                        alt={project.name.replace(' (FF&SF)', '')}
                        className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.target.src = '/api/placeholder/400/300';
                        }}
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                        <Building className="text-gray-400" size={48} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium text-white backdrop-blur-sm ${
                      project.status === 'Ongoing' ? 'bg-blue-500/80' : 'bg-green-500/80'
                    }`}>
                      {project.status}
                    </div>
                  </div>
                  <CardContent className="p-6 bg-gradient-to-br from-white to-gray-50/50">
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600 transition-colors">
                      {project.name.replace(' (FF&SF)', '')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2 flex items-center">
                      <MapPin size={12} className="mr-1" />
                      {project.location}
                    </p>
                    <p className="text-sm text-gray-500 mb-4 flex items-center">
                      <Building size={12} className="mr-1" />
                      {project.area} • {project.type}
                    </p>
                    <p className="text-sm text-gray-700 mb-4 line-clamp-3">{project.description}</p>
                    {project.images?.main && (
                      <Button variant="outline" size="sm" asChild className="w-full group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all duration-300">
                        <Link to={`/projects/${project.slug}`}>
                          View Details
                          <ArrowRight size={14} className="ml-1 transform group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          <div className="text-center mt-12">
            <Button asChild size="lg">
              <Link to="/projects">
                View All Projects <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Sigma HQ Teaser */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
            <Sparkles className="text-blue-400" size={16} />
            <span className="text-blue-400 text-sm font-medium">Beyond Architecture</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            I Build AI That Builds
          </h2>
          <p className="text-slate-400 text-lg mb-6">
            Not just using AI tools. Creating them.
          </p>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            30+ construction projects across two countries used to mean chaos.
            So I built <span className="text-blue-400 font-semibold">Sigma HQ</span> —
            an AI command center that catches scope creep, drafts responses,
            and turns noise into clarity.
          </p>

          <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
            <Link to="/sigma-hq" className="flex items-center gap-2">
              See How It Works
              <ArrowRight size={18} />
            </Link>
          </Button>
        </div>
      </section>

      {/* Client Logos */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Professional Experience</h2>
            <p className="text-lg text-gray-600">Collaborated on projects with industry-leading companies</p>
          </div>
          {clientsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-gray-400" size={32} />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 items-center">
              {clientLogos.map((client, index) => (
                <div key={index} className={`flex items-center justify-center p-4 bg-white rounded-xl hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-gray-200 group animate-fade-in-up hover:-translate-y-1 delay-${Math.floor(index/5) * 100}`}>
                  <div className="w-24 h-16 rounded-lg flex items-center justify-center transition-all duration-300 relative overflow-hidden">
                    {client.logo ? (
                      <img
                        src={client.logo}
                        alt={`${client.name} logo`}
                        className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-50 via-gray-100 to-gray-150 rounded-lg flex items-center justify-center group-hover:from-blue-50 group-hover:via-blue-100 group-hover:to-blue-150 transition-all duration-300 relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 animate-shimmer opacity-0 group-hover:opacity-100"></div>
                        <span className="text-xs font-medium text-gray-700 text-center px-2 leading-tight group-hover:text-blue-700 transition-colors z-10 relative">
                          {client.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Design Excellence, Technical Integrity</h2>
          <p className="text-lg leading-relaxed text-gray-300 mb-8">
            {personalInfo.mission} I believe that the role of the architect and technical office leader
            is not only to ensure precision and efficiency but also to create an environment where
            collaboration, innovation, and quality drive every stage of the project.
          </p>
          <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
            <Link to="/contact">Start a Conversation</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
